import { useEffect, useState } from "react";
import { Layout } from "../components/layout.tsx";
import { useAuth } from "../context/auth.tsx";
import {
  archiveDocument,
  listDocuments,
  reindexDocument,
  type KnowledgeDocument,
  type Page,
} from "../api/knowledge.ts";

const SCOPES = ["company", "client", "project", "product", "technical", "meeting", "contract", "marketing"];
const STATUSES = ["PENDING", "PROCESSING", "READY", "FAILED", "ARCHIVED"];

const STATUS_BADGE: Record<string, string> = {
  PENDING: "bg-yellow-900/40 text-yellow-300",
  PROCESSING: "bg-blue-900/40 text-blue-300",
  READY: "bg-green-900/40 text-green-300",
  FAILED: "bg-red-900/40 text-red-300",
  ARCHIVED: "bg-gray-800 text-gray-400",
};

export function KnowledgeDocumentsPage() {
  const { token } = useAuth();
  const [page, setPage] = useState<Page<KnowledgeDocument> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scopeFilter, setScopeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [actionId, setActionId] = useState<string | null>(null);

  const limit = 25;

  function load() {
    if (token == null) return;
    setLoading(true);
    listDocuments(token, { scope: scopeFilter || undefined, status: statusFilter || undefined, search: search || undefined, limit, offset })
      .then(setPage)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }

  useEffect(load, [token, scopeFilter, statusFilter, search, offset]);

  async function handleReindex(doc: KnowledgeDocument) {
    if (token == null) return;
    setActionId(doc.id);
    try {
      await reindexDocument(token, doc.id);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Reindex failed");
    } finally {
      setActionId(null);
    }
  }

  async function handleArchive(doc: KnowledgeDocument) {
    if (token == null) return;
    if (!confirm(`Archive "${doc.title}"? It will be removed from search.`)) return;
    setActionId(doc.id);
    try {
      await archiveDocument(token, doc.id, doc.version);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Archive failed");
    } finally {
      setActionId(null);
    }
  }

  const docs = page?.items ?? [];
  const total = page?.total ?? 0;

  return (
    <Layout>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Documents</h1>
          <span className="text-sm text-gray-400">{total} total</span>
        </div>

        <div className="flex flex-wrap gap-3">
          <input
            type="text"
            placeholder="Search titles…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
            className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm w-64 focus:outline-none focus:border-blue-500"
          />
          <select
            value={scopeFilter}
            onChange={(e) => { setScopeFilter(e.target.value); setOffset(0); }}
            className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none"
          >
            <option value="">All Scopes</option>
            {SCOPES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setOffset(0); }}
            className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none"
          >
            <option value="">All Statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {loading && <p className="text-gray-400">Loading…</p>}
        {error != null && <p className="text-red-400">{error}</p>}

        <div className="rounded-lg border border-gray-800 overflow-hidden">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-900 text-gray-400 uppercase text-xs tracking-wide">
              <tr>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Scope</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Chunks</th>
                <th className="px-4 py-3 text-right">Vectors</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {docs.length === 0 && !loading && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">No documents found</td></tr>
              )}
              {docs.map((doc) => (
                <tr key={doc.id} className="hover:bg-gray-900/50">
                  <td className="px-4 py-3">
                    <div className="text-white font-medium">{doc.title}</div>
                    {doc.error != null && <div className="text-xs text-red-400 mt-0.5 truncate max-w-xs">{doc.error}</div>}
                  </td>
                  <td className="px-4 py-3 text-gray-400">{doc.scope}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGE[doc.status] ?? ""}`}>{doc.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right text-gray-300">{doc.chunkCount}</td>
                  <td className="px-4 py-3 text-right text-gray-300">{doc.embeddingCount}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      {doc.status !== "ARCHIVED" && (
                        <button
                          onClick={() => handleReindex(doc)}
                          disabled={actionId === doc.id}
                          className="text-xs text-blue-400 hover:text-blue-300 disabled:opacity-50"
                        >
                          Re-index
                        </button>
                      )}
                      {doc.status !== "ARCHIVED" && (
                        <button
                          onClick={() => handleArchive(doc)}
                          disabled={actionId === doc.id}
                          className="text-xs text-red-400 hover:text-red-300 disabled:opacity-50"
                        >
                          Archive
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {total > limit && (
          <div className="flex gap-3 justify-center">
            <button onClick={() => setOffset(Math.max(0, offset - limit))} disabled={offset === 0} className="px-4 py-2 bg-gray-800 text-white rounded disabled:opacity-40 text-sm">
              Previous
            </button>
            <span className="py-2 text-gray-400 text-sm">{offset + 1}–{Math.min(offset + limit, total)} of {total}</span>
            <button onClick={() => setOffset(offset + limit)} disabled={offset + limit >= total} className="px-4 py-2 bg-gray-800 text-white rounded disabled:opacity-40 text-sm">
              Next
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}
